import type { BlockSnapshot } from '@blocksuite/affine/store';

import type { PPTDoc } from './template';

/** The answer is not a deck in the shape the createSlides prompt asks for. */
export class SlideOutlineError extends Error {
  override name = 'SlideOutlineError';
}

/**
 * Reads the deck the createSlides prompt asks for from the note that
 * markdownToSnapshot puts the answer in:
 * deck title > slide title > section title > [image keywords, content].
 * An answer in another shape is rejected as a whole, because reading it
 * partly would silently drop or shift slides.
 */
export function readSlideOutline(note: BlockSnapshot | undefined): PPTDoc[] {
  const [deck, ...rest] = note?.children ?? [];
  if (!deck || rest.length > 0) {
    throw new SlideOutlineError('The answer needs exactly one deck title');
  }
  const slides = deck.children;
  if (slides.length === 0) {
    throw new SlideOutlineError('The answer contains no slides');
  }
  return slides.map((slide, index) => {
    if (slide.children.length === 0) {
      throw new SlideOutlineError(
        `Slide "${textOf(slide)}" contains no sections`
      );
    }
    return {
      isCover: index === 0,
      title: textOf(slide),
      sections: slide.children.map(section => {
        const [keywords, content] = section.children;
        if (!keywords || !content) {
          throw new SlideOutlineError(
            `Section "${textOf(section)}" needs image keywords and content`
          );
        }
        return {
          title: textOf(section),
          keywords: textOf(keywords),
          content: textOf(content),
        };
      }),
    };
  });
}

function textOf(block: BlockSnapshot): string {
  const text = block.props.text as
    | { delta?: { insert?: unknown }[] }
    | undefined;
  const insert = text?.delta?.[0]?.insert;
  return typeof insert === 'string' ? insert : '';
}
